import React, { useContext, useEffect, useState } from "react";
import Switch from "@material-ui/core/Switch";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputLabel from "@material-ui/core/InputLabel";
import {
  Button, styled, FormLabel, TextareaAutosize, Checkbox,
} from "@material-ui/core";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import { makeStyles } from "@material-ui/core/styles";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeAddPaymentModal from "./StripeAddPaymentModal";
import MainContext from "../../context/main/mainContext";
import DemoTeamSummary from "./DemoTeamSummary";
import ToolTip from "../../ui/ToolTip";
import SelectField from "../../ui/SelectField";
import countries from "../../data/countries";
import dates from "../../utilities/dates";
import states from "../../data/states";
import {
  MONTHLY_PRICE_CODE,
  newBillingPriceConfig,
  getPlanSelectionForTeam as getPlanIntervalSelection,
  leadCaptureTierOptions,
} from "../config";
import { extractSubItemsFromTeam } from "../../utilities/extractSubItemsFromTeam";
import { PricingPreview } from "../../components/PricingPreview";

const CustomButton = styled(Button)(({ style }) => ({
  color: "#fff",
  backgroundColor: "#000",
  "&:hover": {
    backgroundColor: "#2D2D2D",
  },
  "&:disabled": {
    backgroundColor: "#9a9a9a60",
  },
  padding: "6px 0px",
  width: 170,
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 8,
  fontFamily: "Inter, sans-serif",
  transition: "0.2s !important",
  ...style,
}));

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    height: 48,
  },
  extendedTextField: {
    width: 300,
  },
  inputSelect: {
    height: 56,
    padding: 5,
  },
  extendedTextField: {
    width: 300,
  },
}));

const InternalDemoTeamForm = () => {
  document.title = "Popl Internal Dash - Edit Subscription";

  const mainContext = useContext(MainContext);
  const classes = useStyles();
  const {
    team,
    stripe_customer,
    stripe_customer_payment_methods,
    getCustomerPaymentMethods,
    previewStripeSubscriptionNewBilling,
    createDemoTeam,
    setTeamPricing,
    downloadQuote,
    resetDemoTeamState,
    postDemoFlowUpgrade,
    postDemoFlowStoredTeamId,
    postDemoTeamCreated,
    isProd,
    setDemoFlowDone,
  } = mainContext;

  const [validationErrors, setValidationErrors] = useState({});
  const internalDemoTeam = team?.team?.internalDemoTeams?.find(({ isDeleted }) => !isDeleted);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isCustomPricing, setIsCustomPricing] = useState(
    !!internalDemoTeam?.pricePerSeatDollars || !!internalDemoTeam?.bucketPercentOff,
  );
  // Inputs
  const [numberSeats, setNumberSeats] = useState(1);
  const [leadsNumber, setLeadsNumber] = useState(team?.team?.leadsPaid || 0);
  const [csvImportEnrichment, setCsvImportEnrichment] = useState(
    team?.team?.isImportEnrichmentEnabled || internalDemoTeam?.csvImportEnrichmentPrice || false,
  );

  const [leadsNumberInput, setLeadsNumberInput] = useState(0);
  const [companyName, setCompanyName] = useState(
    team?.team?.companyName || internalDemoTeam?.companyName || "",
  );
  const [chargeAutomatically, setChargeAutomatically] = useState(false);
  const [selectedPlanInterval, setSelectedPlanInterval] = useState("");
  const [isPriceMonthly, setIsPriceMonthly] = useState(false);
  const [offerFreeTrial, setOfferFreeTrial] = useState(
    (internalDemoTeam?.trialExpiresAtutc || internalDemoTeam?.givenTrialWithoutCc)
    && !internalDemoTeam?.hasPaid
    && !internalDemoTeam?.isExpired,
  );

  const [previewLabel, setPreviewLabel] = useState("");

  const [addressInfo, setAddressInfo] = useState({
    country: stripe_customer?.address?.country || "US",
    zipcode: stripe_customer?.address?.postal_code || "",
    state: stripe_customer?.address?.state || "",
    addressLine: stripe_customer?.address?.line1 || "",
    city: stripe_customer?.address?.city || "",
  });

  const [isInvoiceMode, setIsInvioceMode] = useState(
    team?.stripeCustomer?.subscriptions?.data?.length > 0 && team?.team?.stripeSubscription,
  );

  const [customMemo, setCustomMemo] = useState(internalDemoTeam?.quotedCustomMemo || "");

  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);

  const [trialDate, setTrialDate] = useState(
    dates.formatTrialEnd(internalDemoTeam?.trialExpiresAtUtc || defaultEndDate.toISOString()),
  );

  const [dueDate, setDueDate] = useState(
    dates.formatTrialEnd(internalDemoTeam?.dueDateUtc || defaultEndDate.toISOString()),
  );

  const [billingCycleAnchor, setbillingCycleAnchor] = useState(
    dates.formatTrialEnd(new Date().toISOString()),
  );

  const [subscriptionStart, setSubscriptionStart] = useState(
    dates.formatTrialEnd(new Date().toISOString()),
  );

  const [resetBillingCycleAnchor, setresetBillingCycleAnchor] = useState(false);

  useEffect(() => {
    if (!isInvoiceMode || offerFreeTrial || !billingCycleAnchor) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, billingCycleAnchor: "" }));
      return;
    }

    const isBeforeToday = dates.getDaysBetween(new Date(), billingCycleAnchor) < 0;

    if (isBeforeToday) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        billingCycleAnchor: "Invoice Generation Date must be today or in the future",
      }));
    } else {
      // Valid
      setValidationErrors((prevErrors) => ({ ...prevErrors, billingCycleAnchor: "" }));
    }
  }, [billingCycleAnchor, isInvoiceMode, offerFreeTrial, subscriptionStart]);

  useEffect(() => {
    const subStartIsTodayOrFuture = subscriptionStart && dates.getDaysBetween(new Date(), subscriptionStart) >= 0;

    if (
      isInvoiceMode
      && !offerFreeTrial
      && subStartIsTodayOrFuture
      && !team?.team.stripeSubscription
    ) {
      setbillingCycleAnchor(subscriptionStart);
    }
  }, [subscriptionStart, isInvoiceMode, offerFreeTrial]);

  useEffect(() => {
    if (selectedPlanInterval.code === MONTHLY_PRICE_CODE) {
      setCustomBucketDiscount(0);
      setCustomBucketDiscountChecked(false);
    }
  }, [isCustomPricing, selectedPlanInterval, leadsNumber]);

  useEffect(() => {
    // dueDate must be in the future
    if (dates.getDaysBetween(new Date(), dueDate) < 0) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        dueDate: "Date must be in the future",
      }));
      return;
    }

    // If invoice mode is on, require dueDate to be on or after invoice generation date (billingCycleAnchor)
    if (
      isInvoiceMode
      && !offerFreeTrial
      && billingCycleAnchor
      && dates.getDaysBetween(billingCycleAnchor, dueDate) < 0
    ) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        dueDate: "Invoice due date must be on or after Invoice Generation Date",
      }));
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, dueDate: "" }));
    }
  }, [dueDate, isInvoiceMode, offerFreeTrial, billingCycleAnchor]);

  useEffect(() => {
    if (offerFreeTrial) {
      setIsInvioceMode(false);
    }
    if (offerFreeTrial && dates.getDaysBetween(new Date(), trialDate) < 0) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        trialDate: "Date must be in the future",
      }));
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, trialDate: "" }));
    }
  }, [trialDate, offerFreeTrial]);

  useEffect(() => {
    if (numberSeats < 1 || Number.isNaN(numberSeats)) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        numberSeats: "Number of seats must be greater than 5",
      }));
    } else {
      setValidationErrors((prevErrors) => ({ ...prevErrors, numberSeats: "" }));
    }
  }, [numberSeats]);

  useEffect(() => {
    if (isInvoiceMode) {
      setOfferFreeTrial(false);
    }
  }, [isInvoiceMode]);

  // Custom Only
  const [pricePerSeatDollars, setPricePerSeatDollars] = useState(
    internalDemoTeam?.pricePerSeatDollars ?? 0,
  );

  const [pricePerSeatDollarsChecked, setPricePerSeatDollarsChecked] = useState(
    !!internalDemoTeam?.pricePerSeatDollars ?? false,
  );

  const [customBucketDiscount, setCustomBucketDiscount] = useState(
    internalDemoTeam?.bucketPercentOff ?? 0,
  );

  const [customBucketDiscountChecked, setCustomBucketDiscountChecked] = useState(
    !!internalDemoTeam?.bucketPercentOff ?? false,
  );

  const [dedicatedCsm, setDedicatedCsm] = useState(!!internalDemoTeam?.dedicatedCsmPrice ?? false);
  const [customDedicatedCsmDollars, setCustomDedicatedCsmDollars] = useState(
    internalDemoTeam?.customDedicatedCsmDollars ?? 1000,
  );

  const [customCsvImportEnrichmentDollars, setCustomCsvImportEnrichmentDollars] = useState(
    internalDemoTeam?.customCsvImportEnrichmentDollars ?? 7500,
  );

  const switchPricingMode = (e) => {
    setIsCustomPricing(e.target.checked);
  };

  const handleAddressChange = (field, value) => {
    const maxLengths = {
      addressLine: 255,
      city: 140,
      zipcode: 10,
    };

    // Validate length if field has a max length constraint
    if (maxLengths[field] && value.length > maxLengths[field]) {
      return;
    }

    setAddressInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Updated country and state handlers
  const handleSelectCountry = (event) => {
    handleAddressChange("country", event.target.value);
  };

  const handleSelectState = (event) => {
    handleAddressChange("state", event.target.value);
  };

  const doPreview = async () => {
    setIsLoadingPreview(true);
    await previewStripeSubscriptionNewBilling(
      {
        number_seats: numberSeats,
        price_per_seat_yearly_dollars:
          !isPriceMonthly && isCustomPricing && pricePerSeatDollarsChecked
            ? pricePerSeatDollars
            : null,

        discount_factor:
          !isPriceMonthly && isCustomPricing && customBucketDiscountChecked
            ? 1 - customBucketDiscount / 100
            : 1,
        leadsNumber: !isPriceMonthly ? (leadsNumber === 50001 ? leadsNumberInput : leadsNumber) : 0,
        interval: selectedPlanInterval.interval,
        intervalCount: selectedPlanInterval.intervalCount,
        dedicated_csm: dedicatedCsm,
        customDedicatedCsmDollars:
          !isPriceMonthly && dedicatedCsm ? customDedicatedCsmDollars : 0,
        customCsvImportEnrichmentDollars:
          !isPriceMonthly && csvImportEnrichment
            ? customCsvImportEnrichmentDollars
            : 0,
      },
      (err) => {
        if (err) {
          alert(err);
        }
        setIsLoadingPreview(false);
      },
    );

    setPreviewLabel(selectedPlanInterval.label);
  };

  const doCreate = () => {
    resetDemoTeamState();

    // Set loading to true
    setIsLoading(true);

    // Convert YYYY-MM-DD to days from now
    let trialDays = dates.getDaysBetween(new Date(), trialDate || defaultEndDate);
    let daysUntilDue = dates.getDaysBetween(new Date(), dueDate || defaultEndDate);

    if (isPriceMonthly && offerFreeTrial) {
      trialDays = 30;
    }

    const payload = {
      // Details & Meta
      teamId: postDemoFlowStoredTeamId,
      stripeCustomerId: stripe_customer?.id,
      companyName,
      isUpgrade: postDemoFlowUpgrade,
      // Plan Info
      numberSeats: isPriceMonthly && offerFreeTrial ? null : numberSeats,
      leadsNumber: !isPriceMonthly ? (leadsNumber === 50001 ? leadsNumberInput : leadsNumber) : 0,
      teamOwnerName: stripe_customer?.name || internalDemoTeam?.teamOwnerName || "",
      // Pricing details
      type: selectedPlanInterval.code,
      stripeAddress: addressInfo,
      chargeAutomatically,
      csvImportEnrichment,
      customPricing:
        !isPriceMonthly
        && isCustomPricing
        && (pricePerSeatDollarsChecked || customBucketDiscountChecked),
      pricePerSeatDollars:
        !isPriceMonthly && isCustomPricing && pricePerSeatDollarsChecked
          ? pricePerSeatDollars
          : null,
      discountFactor: 1, // can get rid of soon
      customBucketDiscountFactor:
        !isPriceMonthly && isCustomPricing && customBucketDiscountChecked
          ? 1 - customBucketDiscount / 100
          : 1,
      // Dedicated CSM / CSV custom pricing
      dedicatedCsm,
      customDedicatedCsmDollars:
        !isPriceMonthly && dedicatedCsm ? customDedicatedCsmDollars : null,
      customCsvImportEnrichmentDollars:
        !isPriceMonthly && csvImportEnrichment
          ? customCsvImportEnrichmentDollars
          : null,
      daysUntilDue,
      customMemo,
      // Trial Stuff
      trialDays: offerFreeTrial ? trialDays : 0,
      // Billing Interval
      intervalCount: selectedPlanInterval.intervalCount,
      interval: selectedPlanInterval.interval,
      billingCycleAnchor:
        isInvoiceMode && !offerFreeTrial ? billingCycleAnchor : null,
      resetBillingCycleAnchor:
        isInvoiceMode && !offerFreeTrial && postDemoFlowUpgrade ? resetBillingCycleAnchor : false,
      subscriptionStart,
    };

    const shouldCreateOrUpdateSub = (postDemoFlowUpgrade
      && team?.stripeCustomer?.subscriptions?.data?.length > 0
      && team?.team?.stripeSubscription)
      || isInvoiceMode;

    const fn = shouldCreateOrUpdateSub ? setTeamPricing : createDemoTeam;

    fn(payload, (err) => {
      setIsLoading(false);
    });
  };

  const handleDownloadQuote = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const data = {
      teamId: postDemoFlowStoredTeamId,
      includeCheckout: true,
      companyName,
    };

    await downloadQuote(data, (err) => {
      if (err) {
        alert(`Error download quote: ${err}`);
      }
      setIsLoading(false);
    });
  };

  const baseSubmit = () => {
    doPreview();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    baseSubmit();
  };

  useEffect(() => {
    if (selectedPlanInterval) {
      const priceConfig = newBillingPriceConfig.find(
        (config) => config.code === selectedPlanInterval.code,
      );
      if (priceConfig && priceConfig.code === MONTHLY_PRICE_CODE) {
        setIsPriceMonthly(true);
        setOfferFreeTrial(false);
        setIsCustomPricing(false);
        setLeadsNumber(0);
        setLeadsNumberInput(0);
      } else {
        setIsPriceMonthly(false);
      }
    }
  }, [selectedPlanInterval]);

  useState(() => {
    const { seatsItem } = extractSubItemsFromTeam(team);
    const selection = getPlanIntervalSelection(seatsItem);
    setSelectedPlanInterval(selection);
    setNumberSeats(internalDemoTeam?.numberSeats || seatsItem?.quantity || 1);
  }, [team?.team]);
  // Add payment modal

  const [openStripePaymentModal, setOpenStripePaymentModal] = useState(false);
  const doOpenStripePaymentModal = () => {
    setOpenStripePaymentModal(true);
  };

  const onCloseStripePaymentModal = () => {
    setOpenStripePaymentModal(false);
  };

  const stripeOptions = {
    mode: "setup",
    currency: "usd",
    paymentMethodTypes: ["card"],
    disableLink: true,
  };

  const [stripePromise, setStripePromise] = useState(null);
  useEffect(() => {
    loadStripe(
      isProd
        ? "pk_live_51ISRsnJqkGKmOFO6LLM2GcznWFLHS40RpqGm5N58il5MgKyT3XbfMw0r3jgbpGRcyq24rtr3YrK8Bbd4DaJTO7Pm00dmW3rNlq"
        : "pk_test_51ISRsnJqkGKmOFO6YXwekNU7nltG5qZLswJWgSyQnC78FFt8d5ZxveEdy9bqmVbVPMkxAjeqdc17Wy7sCxYlAPnK00eb2dGR0l",
    ).then((res) => {
      setStripePromise(res);
    });
  }, []);

  useEffect(() => {
    if (stripe_customer?.id) {
      getCustomerPaymentMethods(
        {
          stripeCustomerId: stripe_customer.id,
        },
        (err) => {
          if (err) {
            alert(err);
          }
        },
      );
    }
  }, [stripe_customer]);

  return (
    <div class="flex-container">
      {!!stripePromise && (
        <Elements stripe={stripePromise} options={stripeOptions}>
          <StripeAddPaymentModal
            stripe={stripePromise}
            stripeCustomerId={stripe_customer?.id}
            open={openStripePaymentModal}
            closeModal={onCloseStripePaymentModal}
          ></StripeAddPaymentModal>
        </Elements>
      )}

      {postDemoTeamCreated ? (
        <div class="column">
          <h3>Team Created Successfully</h3>
          <DemoTeamSummary
            companyName={companyName}
            numberSeats={numberSeats}
            leadsNumber={leadsNumber}
            priceId={selectedPlanInterval.code}
            newBillingPriceConfig={newBillingPriceConfig}
            isPriceMonthly={isPriceMonthly}
            isCustomPricing={isCustomPricing}
            pricePerSeatYearlyDollars={pricePerSeatDollars}
            selectedPlanInterval={selectedPlanInterval}
            customBucketDiscount={customBucketDiscount}
            offerFreeTrial={offerFreeTrial}
            trialDate={trialDate}
            csvImportEnrichment={csvImportEnrichment}
          />
          <div style={{ paddingTop: "1rem", display: "flex", gap: "0.75rem" }}>
            <CustomButton style={{ backgroundColor: "#1881e3" }} onClick={handleDownloadQuote}>
              {isLoading ? <CircularProgress color="secondary" size={15} /> : "Download Quote"}
            </CustomButton>
            <CustomButton onClick={setDemoFlowDone}>Complete</CustomButton>
          </div>
        </div>
      ) : (
        <>
          <div class="column">
            <h3>{postDemoFlowUpgrade ? "Change Team Pricing" : "Set New Team Pricing"}</h3>
            <div className="stripeCustomerContainer">
              <div>
                <>
                  <TextField
                    required="true"
                    variant="outlined"
                    id="companyName"
                    value={companyName}
                    className={classes.extendedTextField}
                    label="Company Name"
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={isLoading || isLoadingPreview}
                  />
                  <br />
                </>
                <hr />
                <h4>Plan Details</h4>
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <FormGroup row>
                    <FormControl className={classes.formControl}>
                      <InputLabel id="demo-simple-select-label" required={true}>
                        Plan Interval
                      </InputLabel>
                      <Select
                        value={selectedPlanInterval.label}
                        onChange={(e) => setSelectedPlanInterval(() => newBillingPriceConfig.find((config) => config.label === e.target.value))
                        }
                        disabled={isLoading}
                        required={true}
                      >
                        {newBillingPriceConfig.map((config) => (
                          <MenuItem value={config.label}>{config.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FormGroup>
                  <hr />
                  <h4>Pricing Details</h4>
                  {selectedPlanInterval !== MONTHLY_PRICE_CODE && (
                    <>
                      <FormGroup row>
                        <FormControl className={classes.formControl}>
                          <InputLabel id="demo-simple-select-label">Leads Bucket*</InputLabel>
                          <Select
                            value={leadsNumber}
                            onChange={(e) => {
                              setLeadsNumber(Number.parseInt(e.target.value, 10));
                            }}
                            disabled={isLoading}
                            required={true}
                          >
                            {leadCaptureTierOptions
                              .filter(
                                ({ excludedPlans }) => !excludedPlans.includes(selectedPlanInterval.code),
                              )
                              .map(({ label, value }) => (
                                <MenuItem value={value}>{label}</MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        {leadsNumber === 50001 && (
                          <TextField
                            required="true"
                            variant="outlined"
                            value={leadsNumberInput}
                            type="number"
                            label={"Number of Leads"}
                            onChange={(e) => setLeadsNumberInput(parseInt(e.target.value, 10))}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value, 10);
                              if (value < 50001) {
                                setLeadsNumber(
                                  leadCaptureTierOptions.findLast((item) => item.value <= value)
                                    ?.value,
                                );
                              }
                            }}
                            disabled={isLoading || isLoadingPreview}
                            style={{ marginLeft: 10 }}
                          />
                        )}
                      </FormGroup>
                      <br />
                    </>
                  )}

                  {(!isPriceMonthly || (isPriceMonthly && !offerFreeTrial)) && (
                    <>
                      <TextField
                        style={{ marginBottom: 20 }}
                        required="true"
                        variant="outlined"
                        value={numberSeats}
                        type="number"
                        label={"Number of Members"}
                        onChange={(e) => {
                          const num = Number.parseInt(e.target.value, 10);
                          setNumberSeats(num);
                        }}
                        disabled={isLoading || isLoadingPreview}
                      />
                      {validationErrors.numberSeats && (
                        <p style={{ color: "red", magin: 0 }}>{validationErrors.numberSeats}</p>
                      )}
                    </>
                  )}
                  <></>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={csvImportEnrichment}
                            onChange={(e) => setCsvImportEnrichment(e.target.checked)}
                            name="csvImportEnrichment"
                            inputProps={{ "aria-label": "secondary checkbox" }}
                            disabled={isLoading}
                          />
                        }
                        label={"CSV Import Enrichment"}
                      />
                    </FormGroup>
                    <ToolTip desc="When enabled, this team will have access to the CSV Import Enrichment feature. This is an additional $7,500" />

                    {csvImportEnrichment && (
                      <TextField
                        variant="outlined"
                        required
                        value={customCsvImportEnrichmentDollars ?? ""}
                        type="number"
                        label={"$ Dollars"}
                        onChange={(e) => setCustomCsvImportEnrichmentDollars(Number.parseFloat(e.target.value, 10))
                        }
                        disabled={isLoading || isLoadingPreview}
                        style={{ marginLeft: 10 }}
                      />
                    )}
                  </div>

                  <div style={{ marginTop: 10, display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dedicatedCsm}
                          onChange={(e) => setDedicatedCsm(e.target.checked)}
                        />
                      }
                      label={"Dedicated CSM"}
                    />
                    {dedicatedCsm && (
                      <TextField
                        variant="outlined"
                        required
                        value={customDedicatedCsmDollars ?? ""}
                        type="number"
                        label={"$ Dollars"}
                        onChange={(e) => setCustomDedicatedCsmDollars(Number.parseFloat(e.target.value, 10))
                        }
                        disabled={isLoading || isLoadingPreview}
                        style={{ marginLeft: 10 }}
                      />
                    )}
                  </div>
                  {isPriceMonthly ? (
                    <p>Custom Pricing not available for monthlies</p>
                  ) : (
                    <>
                      <FormGroup row style={{ alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isCustomPricing}
                              onChange={switchPricingMode}
                              name="pricingMode"
                              inputProps={{ "aria-label": "secondary checkbox" }}
                              disabled={isLoading}
                            />
                          }
                          label={"Custom Pricing"}
                        />

                        <ToolTip desc="Check the custom pricing option you need to enable" />
                      </FormGroup>

                      {isCustomPricing && (
                        <div style={{ marginTop: 10 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!pricePerSeatDollarsChecked}
                                onChange={(e) => {
                                  setPricePerSeatDollarsChecked(e.target.checked);
                                }}
                              />
                            }
                            label=""
                          />
                          <TextField
                            variant="outlined"
                            required
                            value={pricePerSeatDollars ?? ""}
                            type="number"
                            label={`$ Per Seat Per ${selectedPlanInterval.intervalCount} ${selectedPlanInterval.intervalCount === 1 ? selectedPlanInterval.interval : `${selectedPlanInterval.interval}s`}`}
                            onChange={(e) => setPricePerSeatDollars(Number.parseFloat(e.target.value, 10))}
                            disabled={
                              isLoading
                              || isLoadingPreview
                              || pricePerSeatDollars === null
                              || !pricePerSeatDollarsChecked
                            }
                          />
                          <br />
                          <br />
                        </div>
                      )}

                      {isCustomPricing && leadsNumber > 0 && (
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!customBucketDiscountChecked}
                                onChange={(e) => setCustomBucketDiscountChecked(e.target.checked)}
                              />
                            }
                            label=""
                          />
                          <TextField
                            variant="outlined"
                            required
                            value={customBucketDiscount ?? ""}
                            type="number"
                            label={"Bucket Discount %"}
                            onChange={(e) => setCustomBucketDiscount(Number.parseFloat(e.target.value, 10))
                            }
                            disabled={
                              isLoading
                              || isLoadingPreview
                              || customBucketDiscount === null
                              || !customBucketDiscountChecked
                            }
                          />
                          <br />
                        </div>
                      )}
                    </>
                  )}

                  <br />
                </form>
                <div
                  style={{ backgroundColor: "#1881e3" }}
                  className="buttonContainer"
                  onClick={isLoading || isLoadingPreview ? null : baseSubmit}
                >
                  {isLoadingPreview ? (
                    <CircularProgress color="secondary" size={15} />
                  ) : (
                    "Preview Pricing"
                  )}
                </div>
                <hr />
                <h4 style={{ marginBottom: 0 }}>Billing Details</h4>
                <div style={{ display: "flex", gap: "2rem" }}>
                  <FormGroup row>
                    <SelectField
                      label="Country / Region"
                      name="country"
                      dataset={countries}
                      initialValue={addressInfo.country}
                      width={448}
                      onChange={handleSelectCountry}
                      styles={{ root: { width: 200 } }}
                    />
                  </FormGroup>
                  {addressInfo.country === "US" && (
                    <FormGroup row>
                      <SelectField
                        label="State"
                        name="state"
                        dataset={states}
                        initialValue={addressInfo.state}
                        placeholder="Choose a state"
                        width={448}
                        onChange={handleSelectState}
                        styles={{ root: { width: 200 } }}
                      />
                    </FormGroup>
                  )}
                </div>
                <div>
                  <FormGroup row>
                    <TextField
                      variant="outlined"
                      value={addressInfo.addressLine}
                      className={classes.extendedTextField}
                      type="text"
                      label={"Address Line"}
                      onChange={(e) => handleAddressChange("addressLine", e.target.value)}
                      disabled={isLoading || isLoadingPreview}
                    />
                  </FormGroup>
                  <br />
                  <FormGroup row>
                    <TextField
                      variant="outlined"
                      value={addressInfo.city}
                      className={classes.extendedTextField}
                      type="text"
                      label={"City"}
                      onChange={(e) => handleAddressChange("city", e.target.value)}
                      disabled={isLoading || isLoadingPreview}
                    />
                  </FormGroup>
                  <br />

                  <FormGroup row>
                    <TextField
                      variant="outlined"
                      value={addressInfo.zipcode}
                      className={classes.extendedTextField}
                      type="text"
                      required={addressInfo.country === "US"}
                      label="Zip Code"
                      onChange={(e) => handleAddressChange("zipcode", e.target.value)}
                      disabled={isLoading || isLoadingPreview}
                    />
                  </FormGroup>
                </div>
                {isInvoiceMode && !offerFreeTrial && (
                  <>
                    {!team?.team?.stripeSubscription && (
                      <>
                        <div className="textInputContainer">
                          <FormControl style={{ marginTop: "0.75rem" }}>
                            <FormLabel>Subscription Start Date</FormLabel>
                            <TextField
                              variant="outlined"
                              required={true}
                              onChange={(e) => {
                                if (e.target.value.includes(" ")) return;
                                setSubscriptionStart(e.target.value);
                              }}
                              value={subscriptionStart}
                              type="date"
                              disabled={isLoading || isLoadingPreview}
                            />
                          </FormControl>

                          {validationErrors.subscriptionStartDate && (
                            <p style={{ color: "red" }}>{validationErrors.subscriptionStartDate}</p>
                          )}
                        </div>
                        <div className="textInputContainer" style={{ marginTop: "1rem" }}>
                          <FormControl>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <FormLabel>Invoice Generation Date</FormLabel>
                              <ToolTip desc="Invoice Generation Date is locked to Subscription Start Date when the subscription start date is today or in the future. It can only be set manually for backdated (past) subscriptions." />
                            </div>
                            <TextField
                              variant="outlined"
                              required={true}
                              onChange={(e) => {
                                if (e.target.value.includes(" ")) return;
                                // If subscriptionStartDate is today or in the future we lock the invoice
                                // generation date to it, so prevent manual changes in that case.
                                if (
                                  dates.getDaysBetween(new Date(), subscriptionStart) >= 0
                                  && !postDemoFlowUpgrade
                                ) return;
                                setbillingCycleAnchor(e.target.value);
                              }}
                              value={billingCycleAnchor}
                              type="date"
                              disabled={
                                isLoading
                                || isLoadingPreview
                                || (dates.getDaysBetween(new Date(), subscriptionStart) >= 0
                                  && !team?.team?.stripeSubscription)
                              }
                            />
                          </FormControl>
                          {validationErrors.billingCycleAnchor && (
                            <p style={{ color: "red" }}>{validationErrors.billingCycleAnchor}</p>
                          )}
                        </div>
                      </>
                    )}
                    <div className="textInputContainer">
                      <FormControl>
                        <FormLabel>Quote/Invoice Due Date</FormLabel>
                        <TextField
                          variant="outlined"
                          required="true"
                          onChange={(e) => {
                            if (e.target.value.includes(" ")) return;
                            setDueDate(e.target.value);
                          }}
                          value={dueDate}
                          type="date"
                        />
                      </FormControl>
                    </div>
                    {validationErrors.dueDate && (
                      <p style={{ color: "red" }}>{validationErrors.dueDate}</p>
                    )}

                    {team?.team?.stripeSubscription && (
                      <div style={{ display: "flex", alignItems: "center", marginTop: "1rem" }}>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={resetBillingCycleAnchor}
                                onChange={(e) => setresetBillingCycleAnchor(e.target.checked)}
                                name="resetBillingCycleAnchor"
                                inputProps={{ "aria-label": "secondary checkbox" }}
                                disabled={isLoading}
                              />
                            }
                            label={"Reset Billing Cycle"}
                          />
                        </FormGroup>
                        <ToolTip desc="When checked, the billing anchor will be reset to the current date" />
                      </div>
                    )}
                  </>
                )}
                <FormGroup>
                  <FormLabel>Custom Memo (Optional)</FormLabel>
                  <TextareaAutosize
                    variant="outlined"
                    value={customMemo}
                    type="text"
                    minRows={4}
                    style={{
                      width: 700,
                      fontSize: 15,
                      font: "inherit",
                      padding: "0.5rem",
                      margin: "0.5rem 0",
                    }}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setCustomMemo(String(e.target.value));
                      }
                    }}
                    disabled={isLoading}
                  />
                </FormGroup>
                <br />
                {stripe_customer_payment_methods?.length > 0 ? (
                  <p>Payment method on file</p>
                ) : (
                  <>
                    {stripe_customer?.id && (
                      <>
                        <CustomButton
                          onClick={isLoading || isLoadingPreview ? null : doOpenStripePaymentModal}
                        >
                          Add Payment Method
                        </CustomButton>

                        <br />
                      </>
                    )}
                  </>
                )}
                <br />
                <hr />
                {(stripe_customer?.subscriptions?.data?.length || 0) < 1 && !offerFreeTrial && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isInvoiceMode}
                            onChange={(e) => setIsInvioceMode(e.target.checked)}
                            name="invoice-mode"
                            inputProps={{ "aria-label": "secondary checkbox" }}
                            disabled={isLoading}
                          />
                        }
                        label={"Invoice Mode"}
                      />
                      <br />
                      <br />
                      <br />
                    </FormGroup>

                    <ToolTip desc="When turned on, a subscription and invoice will be generated instead of a quote." />
                  </div>
                )}

                <div>
                  {isInvoiceMode
                    && !offerFreeTrial
                    && (stripe_customer_payment_methods?.length || 0) > 0 && (
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={chargeAutomatically}
                            onChange={(e) => setChargeAutomatically(e.target.checked)}
                            name="chargeAutomatically"
                            inputProps={{ "aria-label": "secondary checkbox" }}
                            disabled={isLoading}
                          />
                        }
                        label={"Charge Automatically"}
                      />
                      <br />
                    </FormGroup>
                  )}

                  {!chargeAutomatically && !isPriceMonthly && !isInvoiceMode && (
                    <>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={offerFreeTrial}
                              onChange={(e) => setOfferFreeTrial(e.target.checked)}
                              name="offerFreeTrial"
                              inputProps={{ "aria-label": "secondary checkbox" }}
                              disabled={isLoading || isInvoiceMode}
                            />
                          }
                          label={"Offer Free Trial"}
                        />
                        <br />
                      </FormGroup>
                      {offerFreeTrial && (
                        <>
                          <div className="textInputContainer">
                            <FormControl>
                              <FormLabel>Trial End Date</FormLabel>
                              <TextField
                                variant="outlined"
                                required="true"
                                onChange={(e) => {
                                  if (e.target.value.includes(" ")) return;
                                  setTrialDate(e.target.value);
                                }}
                                value={trialDate}
                                type="date"
                              />
                            </FormControl>
                          </div>
                          {validationErrors.trialDate && (
                            <p style={{ color: "red" }}>{validationErrors.trialDate}</p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div
                  style={{
                    paddingBottom: "1rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                ></div>
                <CustomButton
                  onClick={isLoading || isLoadingPreview ? null : doCreate}
                  disabled={
                    Object.values(validationErrors).find((val) => val)
                    || isLoading
                    || !companyName
                    || Number.isNaN(numberSeats)
                    || !dueDate
                    || !selectedPlanInterval
                    || (isCustomPricing && Number.isNaN(pricePerSeatDollars))
                    || (addressInfo.country === "US" && !addressInfo.zipcode)
                    || (!offerFreeTrial && !isInvoiceMode)
                    || (isInvoiceMode
                      && !offerFreeTrial
                      && !postDemoFlowUpgrade
                      && !billingCycleAnchor)
                  }
                >
                  {isLoading ? (
                    <CircularProgress color="secondary" size={15} />
                  ) : postDemoFlowUpgrade ? (
                    "Update Team"
                  ) : (
                    "Create Team"
                  )}
                </CustomButton>
              </div>
              <br />
            </div>
          </div>
          <PricingPreview isLoadingPreview={isLoadingPreview} previewLabel={previewLabel} />
        </>
      )}
    </div>
  );
};

export default InternalDemoTeamForm;
